import { useEffect, useState } from "react";
import { ArrowUpRight, MapPin, Mail, Check } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "./ui/accordion";
import { Reveal, Eyebrow } from "./Reveal";
import { JOBS } from "../data/content";
import { useSite } from "../context/SiteContext";
import { api } from "../lib/api";

const applyLink = (role, email) => `mailto:${email}?subject=${encodeURIComponent(`Application — ${role}`)}`;

const JobDetails = ({ job, i, hr }) => (
  <div className="space-y-7 pb-8 pr-2 text-ink/75">
    <div>
      <h4 className="font-display text-base text-ink">About the Role</h4>
      <p className="mt-2 leading-relaxed">{job.about}</p>
    </div>
    {job.lists.map((l) => (
      <div key={l.heading}>
        <h4 className="font-display text-base text-ink">{l.heading}</h4>
        <ul className="mt-3 space-y-2">
          {l.items.map((it) => (
            <li key={it} className="flex gap-3 leading-relaxed">
              <Check className="mt-1 h-4 w-4 shrink-0 text-clay" />
              <span>{it}</span>
            </li>
          ))}
        </ul>
      </div>
    ))}
    <div className="flex flex-col gap-4 rounded-2xl bg-white p-6 sm:flex-row sm:items-center sm:justify-between">
      <p className="text-ink">
        <span className="font-semibold">{job.cta}</span> Submit your CV to{" "}
        <a href={`mailto:${hr}`} className="text-clay underline-offset-4 hover:underline">{hr}</a>
      </p>
      <a href={applyLink(job.title, hr)} data-testid={`job-apply-${i}`} className="btn-primary shrink-0 justify-center">
        Apply Now <ArrowUpRight className="h-4 w-4" />
      </a>
    </div>
  </div>
);

export const Careers = () => {
  const hr = useSite().contact.hr_email;
  const [jobs, setJobs] = useState(JOBS);
  useEffect(() => {
    api.get("/jobs").then((r) => setJobs(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);
  return (
  <section id="careers" data-testid="careers-section" className="bg-mint/60 py-24 sm:py-32">
    <div className="mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 lg:grid-cols-12">
      <div className="lg:col-span-4">
        <Reveal>
          <Eyebrow>Careers · We're hiring</Eyebrow>
          <h2 className="font-display text-4xl leading-[1.08] text-ink sm:text-5xl">Grow with Bali's hospitality team.</h2>
          <p className="mt-6 text-base leading-relaxed text-ink/70 sm:text-lg">
            Join a team managing 37 hotels and villas across 7 Bali regions. Explore our open positions and apply today.
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <a href={applyLink("General Application", hr)} data-testid="careers-general-apply" className="btn-primary mt-8">
            <Mail className="h-4 w-4" /> Send your CV
          </a>
          <p className="mt-3 text-sm text-ink/60">Applications go to {hr}</p>
        </Reveal>
      </div>
      <Reveal delay={0.1} className="lg:col-span-8">
        <Accordion type="single" collapsible className="border-t border-ink/15" data-testid="job-list">
          {jobs.length === 0 && (
            <p className="py-8 text-ink/70" data-testid="no-jobs">There are no open positions right now — you're welcome to send your CV for future opportunities.</p>
          )}
          {jobs.map((job, i) => (
            <AccordionItem key={job.id || job.title} value={`job-${i}`} className="border-ink/15">
              <AccordionTrigger data-testid={`job-row-${i}`} className="py-6 text-left hover:no-underline">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-clay">{job.dept}</p>
                  <h3 className="mt-1 font-display text-xl text-ink sm:text-2xl">{job.title}</h3>
                  <div className="mt-2 flex flex-wrap gap-4 text-sm font-normal text-ink/60">
                    {(job.meta || []).map((m) => (
                      <span key={m} className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />{m}</span>
                    ))}
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="text-[15px]">
                <JobDetails job={job} i={i} hr={hr} />
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Reveal>
    </div>
  </section>
  );
};
